import { ReactNode } from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@carbon/react";
import { Download } from "@carbon/icons-react";
import { useTranslation } from "react-i18next";
import { currencyFormatter } from "../../utils";
import { OfferResponse, OrderResponse } from "../../types";
import { useReadFile } from "../../hooks";
import { useUser } from "../../context/hooks";
import "./bid.scss";

const MATERIAL_NAME_MAX = 20;

const truncateMaterialName = (value?: string | null): string => {
  if (!value) return "";
  return value.length > MATERIAL_NAME_MAX
    ? `${value.substring(0, MATERIAL_NAME_MAX)}…`
    : value;
};

type BidResultTableProps = {
  title: string;
  motive?: string;
  /** S3 key of the optional supporting file attached at award time (buyer adjudication view). */
  fileUrl?: string;
  selectedOffers?: OfferResponse[];
  offer?: OfferResponse;
  ordersWithoutOffers?: OrderResponse[];
};

/**
 * The awarded/non-awarded bid-result comparison (Task 7) — Carbon `Table` parts in place of the
 * pre-Carbon raw `<table>`. Composed from the same parts `DataTable.tsx` uses internally, but not
 * through that shared primitive: this is a fixed, un-paginated, un-searchable comparison view
 * (not a resource list), and it needs two visually distinct tables (awarded / not-awarded) side
 * by side under one title, which the generic `DataTable` contract doesn't express.
 */
const BidResultTable = ({
  title,
  motive,
  fileUrl,
  selectedOffers,
  offer,
  ordersWithoutOffers,
}: BidResultTableProps) => {
  const { isBuy } = useUser();
  const { t } = useTranslation();
  const { mutate: readFile, isPending: isDownloading } = useReadFile();

  const headers = [
    t("tenders.tenderSummary.ean"),
    t("tenders.tenderSummary.material"),
    t("tenders.tenderSummary.requestedVolume"),
    t("tenders.tenderSummary.awardedVolume"),
    t("tenders.tenderSummary.offeredPrice"),
    t("tenders.tenderSummary.awardedPrice"),
    t("tenders.tenderSummary.company"),
  ];

  const rowFor = (
    key: string,
    material: { ean?: string; globalMaterialName?: string } | undefined,
    requestedVolume: ReactNode,
    awardedVolume: ReactNode,
    offeredPrice: ReactNode,
    awardedPrice: ReactNode,
    company: ReactNode
  ) => (
    <TableRow key={key}>
      <TableCell className="bid-result-table__ean">
        {material?.ean || "-"}
      </TableCell>
      <TableCell title={material?.globalMaterialName ?? ""}>
        {truncateMaterialName(material?.globalMaterialName)}
      </TableCell>
      <TableCell>{requestedVolume}</TableCell>
      <TableCell>{awardedVolume}</TableCell>
      <TableCell>{offeredPrice}</TableCell>
      <TableCell>{awardedPrice}</TableCell>
      <TableCell>{company}</TableCell>
    </TableRow>
  );

  return (
    <div className="bid-result-table">
      <p className="bid-result-table__title">{title}</p>
      <div className="bid-result-table__panel bid-result-table__panel--awarded">
        <Table useZebraStyles={false} size="sm">
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableHeader key={header}>{header}</TableHeader>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedOffers?.map((off) =>
              rowFor(
                off?.order?.id,
                off?.order?.material,
                `${off?.order?.quantity ?? ""} ${off?.order?.measurementUnit ?? ""}`,
                `${off?.quantity ?? ""} ${off?.order?.measurementUnit ?? ""}`,
                currencyFormatter(off?.oldPrice, off?.currency),
                currencyFormatter(off?.price, off?.currency),
                isBuy
                  ? off?.user?.company?.name
                  : off?.order?.user?.company?.name
              )
            )}
            {offer &&
              rowFor(
                offer?.order?.id,
                offer?.order?.material,
                `${offer?.order?.quantity ?? ""} ${offer?.order?.measurementUnit ?? ""}`,
                `${offer?.quantity ?? ""} ${offer?.order?.measurementUnit ?? ""}`,
                currencyFormatter(offer?.oldPrice, offer?.currency),
                currencyFormatter(offer?.price, offer?.currency),
                isBuy
                  ? offer?.user?.company?.name
                  : offer?.order?.user?.company?.name
              )}
          </TableBody>
        </Table>
      </div>

      {ordersWithoutOffers && ordersWithoutOffers.length > 0 && (
        <>
          <p className="bid-result-table__title bid-result-table__title--pending">
            {t("tenders.data.notAwarded")}
          </p>
          <div className="bid-result-table__panel bid-result-table__panel--pending">
            <Table useZebraStyles={false} size="sm">
              <TableHead>
                <TableRow>
                  {headers.map((header) => (
                    <TableHeader key={header}>{header}</TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {ordersWithoutOffers.map((order) =>
                  rowFor(
                    order?.id,
                    order?.material,
                    `${order?.quantity ?? ""} ${order?.measurementUnit ?? ""}`,
                    "-",
                    "-",
                    "-",
                    "-"
                  )
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {(motive || fileUrl) && (
        <div className="bid-result-table__justification">
          <p className="bid-result-table__justification-title">
            {t("award.justificationTitle")}
          </p>
          {motive && (
            <div className="bid-result-table__justification-field">
              <span className="bid-result-table__justification-label">
                {t("tenders.form.offerSelectionComment")}
              </span>
              <p className="bid-result-table__justification-value">{motive}</p>
            </div>
          )}
          {fileUrl && (
            <div className="bid-result-table__justification-field">
              <span className="bid-result-table__justification-label">
                {t("award.fileSectionTitle")}
              </span>
              <Button
                kind="ghost"
                size="sm"
                renderIcon={Download}
                disabled={isDownloading}
                onClick={() => readFile(fileUrl)}
              >
                {t("cta.download")}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BidResultTable;
